"use client";

/**
 * What shuts the pill on a task's screen, and what its idle field says: the
 * task box's own rules, read from the same leaves the box reads (R-F3: "the
 * pending-ask lock disables the pill exactly when it disables the box today").
 * Stores only: BashPill calls this on every route, provider or none.
 */
import { useTaskComposerGateFor } from "@/components/composer/useTaskComposerGate";
import { useLaneRecipient } from "@/components/message-input/useLaneRecipient";
import { useSendPause } from "@/components/message-input/ConnectionHints";
import { deskPlaceholder } from "@/lib/desk-placeholder";

/** The task box's own idle words. */
export const TASK_PLACEHOLDER = "@ for agents / for skills";
/** A lane the spawn never gave an inbox (D8): the box says why rather than
 *  quietly taking a message nobody would read. */
export const NO_INBOX_WORDS = "This helper doesn't take messages. Tell Bash.";
const DEPLOY_WORDS = "Sends after the update";

export interface PillTaskLock {
  /** Nothing may leave: a blocking ask on the screen in view, a lane with no inbox, or a deploy pause. */
  blocked: boolean;
  /** Why, for the key's title; undefined while open. */
  reason: string | undefined;
  /** The lane in view has no inbox: the field itself is shut, as the box's was. */
  fieldDisabled: boolean;
  /** What the idle field says: the task box's own words. */
  placeholder: string;
  /** The lane whose screen has the column, or null for Bash. */
  laneInView: string | null;
  /** R5: the words as they leave, addressed to the lane in view exactly once. */
  addressToLane: (text: string) => string;
  deployPaused: boolean;
  offline: boolean;
}

/** `taskId` null (the pill is not in task mode) = an open, inert lock. */
export function usePillTaskLock(taskId: string | null): PillTaskLock {
  // Handed the pill's own task, never left to read the store's selection: off
  // a task's screen that is null, and a lingering selection's asks cost nothing.
  const gate = useTaskComposerGateFor(taskId);
  const lane = useLaneRecipient(taskId);
  const { deployPaused, offline } = useSendPause();
  const active = taskId !== null;
  if (!active) {
    return {
      blocked: false,
      reason: undefined,
      fieldDisabled: false,
      placeholder: TASK_PLACEHOLDER,
      laneInView: null,
      addressToLane: lane.addressToLane,
      deployPaused: false,
      offline: false,
    };
  }
  const blocked = gate.blocking !== null || lane.laneHasNoInbox || deployPaused;
  const reason = lane.laneHasNoInbox
    ? NO_INBOX_WORDS
    : (gate.lockedWords ?? (deployPaused ? DEPLOY_WORDS : undefined));
  // The box's order: a shut lane says why; a shut box says where to answer; an
  // open lane names itself; the desk's ask invites the answer; else the default.
  const placeholder = lane.laneHasNoInbox
    ? NO_INBOX_WORDS
    : (gate.lockedWords ??
      (lane.laneRecipient
        ? `Message ${lane.laneRecipient.title}`
        : gate.showDesk && gate.pending
          ? deskPlaceholder(gate.pending)
          : TASK_PLACEHOLDER));
  return {
    blocked,
    reason,
    fieldDisabled: lane.laneHasNoInbox,
    placeholder,
    laneInView: lane.laneInView,
    addressToLane: lane.addressToLane,
    deployPaused,
    offline,
  };
}
